import React from "react";
import {
  FaHeading,
  FaParagraph,
  FaListUl,
  FaImage,
  FaCode,
  FaTable,
  FaTrash,
  FaArrowUp,
  FaArrowDown,
  FaPlus,
  FaMinus,
  FaColumns,
} from "react-icons/fa";
import {
  BLOCK_TYPES,
  createEmptyBlock,
} from "../../../utils/theoryContentUtils";

/**
 * TheoryContentEditor - A user-friendly block-based editor for theory content
 * Allows non-technical users to add/edit content without knowing special syntax
 */
export default function TheoryContentEditor({ blocks, setBlocks }) {
  // Add a new block of specified type
  const addBlock = (type) => {
    const newBlock = createEmptyBlock(type);
    setBlocks([...blocks, newBlock]);
  };

  // Update a specific block
  const updateBlock = (id, updates) => {
    setBlocks(
      blocks.map((block) =>
        block.id === id ? { ...block, ...updates } : block
      )
    );
  };

  // Remove a block
  const removeBlock = (id) => {
    setBlocks(blocks.filter((block) => block.id !== id));
  };

  // Move block up
  const moveBlockUp = (index) => {
    if (index === 0) return;
    const newBlocks = [...blocks];
    [newBlocks[index - 1], newBlocks[index]] = [
      newBlocks[index],
      newBlocks[index - 1],
    ];
    setBlocks(newBlocks);
  };

  // Move block down
  const moveBlockDown = (index) => {
    if (index === blocks.length - 1) return;
    const newBlocks = [...blocks];
    [newBlocks[index], newBlocks[index + 1]] = [
      newBlocks[index + 1],
      newBlocks[index],
    ];
    setBlocks(newBlocks);
  };

  // Render individual block based on type
  const renderBlock = (block, index) => {
    const blockTypeLabels = {
      [BLOCK_TYPES.HEADING]: { label: "Heading", icon: FaHeading, color: "purple" },
      [BLOCK_TYPES.PARAGRAPH]: { label: "Paragraph", icon: FaParagraph, color: "blue" },
      [BLOCK_TYPES.LIST]: { label: "Bullet List", icon: FaListUl, color: "green" },
      [BLOCK_TYPES.IMAGE]: { label: "Image", icon: FaImage, color: "pink" },
      [BLOCK_TYPES.CODE]: { label: "Code Block", icon: FaCode, color: "orange" },
      [BLOCK_TYPES.TABLE]: { label: "Table", icon: FaTable, color: "teal" },
    };

    const typeInfo = blockTypeLabels[block.type] || blockTypeLabels[BLOCK_TYPES.PARAGRAPH];
    const IconComponent = typeInfo.icon;

    return (
      <div
        key={block.id}
        className={`border-2 rounded-lg overflow-hidden bg-white shadow-sm border-${typeInfo.color}-200`}
      >
        {/* Block Header */}
        <div className={`flex items-center justify-between px-4 py-2 bg-${typeInfo.color}-50 border-b border-${typeInfo.color}-200`}>
          <div className="flex items-center gap-2">
            <IconComponent className={`text-${typeInfo.color}-600`} />
            <span className={`text-sm font-semibold text-${typeInfo.color}-700`}>
              {typeInfo.label}
            </span>
            <span className="text-xs text-gray-500">#{index + 1}</span>
          </div>
          <div className="flex items-center gap-1">
            {/* Move Up Button */}
            <button
              type="button"
              onClick={() => moveBlockUp(index)}
              disabled={index === 0}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Move up"
            >
              <FaArrowUp size={12} />
            </button>
            {/* Move Down Button */}
            <button
              type="button"
              onClick={() => moveBlockDown(index)}
              disabled={index === blocks.length - 1}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Move down"
            >
              <FaArrowDown size={12} />
            </button>
            {/* Delete Button */}
            <button
              type="button"
              onClick={() => removeBlock(block.id)}
              className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors ml-2"
              title="Delete block"
            >
              <FaTrash size={12} />
            </button>
          </div>
        </div>

        {/* Block Content */}
        <div className="p-4">
          {block.type === BLOCK_TYPES.HEADING && (
            <HeadingBlock block={block} updateBlock={updateBlock} />
          )}
          {block.type === BLOCK_TYPES.PARAGRAPH && (
            <ParagraphBlock block={block} updateBlock={updateBlock} />
          )}
          {block.type === BLOCK_TYPES.LIST && (
            <ListBlock block={block} updateBlock={updateBlock} />
          )}
          {block.type === BLOCK_TYPES.IMAGE && (
            <ImageBlock block={block} updateBlock={updateBlock} />
          )}
          {block.type === BLOCK_TYPES.CODE && (
            <CodeBlock block={block} updateBlock={updateBlock} />
          )}
          {block.type === BLOCK_TYPES.TABLE && (
            <TableBlock block={block} updateBlock={updateBlock} />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 border-t pt-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h3 className="text-lg font-semibold text-gray-900">Theory Content</h3>
        <p className="text-sm text-gray-500">
          Add content blocks using the buttons below
        </p>
      </div>

      {/* Add Block Buttons */}
      <div className="flex flex-wrap gap-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <span className="text-sm font-medium text-gray-600 mr-2 self-center">
          Add:
        </span>
        <button
          type="button"
          onClick={() => addBlock(BLOCK_TYPES.HEADING)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
        >
          <FaHeading size={12} /> Heading
        </button>
        <button
          type="button"
          onClick={() => addBlock(BLOCK_TYPES.PARAGRAPH)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
        >
          <FaParagraph size={12} /> Paragraph
        </button>
        <button
          type="button"
          onClick={() => addBlock(BLOCK_TYPES.LIST)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
        >
          <FaListUl size={12} /> Bullet List
        </button>
        <button
          type="button"
          onClick={() => addBlock(BLOCK_TYPES.IMAGE)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-pink-100 text-pink-700 rounded-lg hover:bg-pink-200 transition-colors text-sm font-medium"
        >
          <FaImage size={12} /> Image
        </button>
        <button
          type="button"
          onClick={() => addBlock(BLOCK_TYPES.CODE)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors text-sm font-medium"
        >
          <FaCode size={12} /> Code Block
        </button>
        <button
          type="button"
          onClick={() => addBlock(BLOCK_TYPES.TABLE)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition-colors text-sm font-medium"
        >
          <FaTable size={12} /> Table
        </button>
      </div>

      {/* Blocks List */}
      {blocks.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <FaParagraph className="mx-auto text-4xl text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No content blocks yet</p>
          <p className="text-gray-400 text-sm mt-1">
            Click the buttons above to start adding content
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {blocks.map((block, index) => renderBlock(block, index))}
        </div>
      )}
    </div>
  );
}

// ============ Individual Block Components ============

function HeadingBlock({ block, updateBlock }) {
  return (
    <input
      type="text"
      value={block.value || ""}
      onChange={(e) => updateBlock(block.id, { value: e.target.value })}
      placeholder="Enter heading text..."
      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg font-semibold transition-all"
    />
  );
}

function ParagraphBlock({ block, updateBlock }) {
  return (
    <textarea
      value={block.value || ""}
      onChange={(e) => updateBlock(block.id, { value: e.target.value })}
      placeholder="Write paragraph content here..."
      rows="4"
      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-y"
    />
  );
}

function ListBlock({ block, updateBlock }) {
  const items = block.items || [""];

  const updateItem = (index, value) => {
    const newItems = [...items];
    newItems[index] = value;
    updateBlock(block.id, { items: newItems });
  };

  const addItem = () => {
    updateBlock(block.id, { items: [...items, ""] });
  };

  const removeItem = (index) => {
    if (items.length <= 1) return;
    updateBlock(block.id, { items: items.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <span className="text-green-600 font-bold">•</span>
          <input
            type="text"
            value={item}
            onChange={(e) => updateItem(index, e.target.value)}
            placeholder={`List item ${index + 1}...`}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
          />
          {items.length > 1 && (
            <button
              type="button"
              onClick={() => removeItem(index)}
              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
              title="Remove item"
            >
              <FaTrash size={12} />
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        className="flex items-center gap-1.5 px-3 py-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors text-sm font-medium mt-2"
      >
        <FaPlus size={10} /> Add item
      </button>
    </div>
  );
}

function ImageBlock({ block, updateBlock }) {
  const [showPreview, setShowPreview] = React.useState(true);
  const [imageError, setImageError] = React.useState(false);

  const handleUrlChange = (e) => {
    updateBlock(block.id, { url: e.target.value });
    setImageError(false);
  };

  return (
    <div className="space-y-3">
      <input
        type="url"
        value={block.url || ""}
        onChange={handleUrlChange}
        placeholder="Enter image URL (e.g., https://example.com/image.png)"
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all"
      />

      {/* Image Preview */}
      {block.url && showPreview && (
        <div className="relative">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500 font-medium">Preview:</span>
            <button
              type="button"
              onClick={() => setShowPreview(false)}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Hide preview
            </button>
          </div>
          {!imageError ? (
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 p-2">
              <img
                src={block.url}
                alt="Preview"
                className="max-w-full max-h-48 mx-auto object-contain rounded"
                onError={() => setImageError(true)}
              />
            </div>
          ) : (
            <div className="border border-red-200 rounded-lg p-4 bg-red-50 text-center">
              <p className="text-red-600 text-sm">
                Failed to load image. Please check the URL.
              </p>
            </div>
          )}
        </div>
      )}

      {block.url && !showPreview && (
        <button
          type="button"
          onClick={() => setShowPreview(true)}
          className="text-xs text-pink-600 hover:text-pink-700"
        >
          Show preview
        </button>
      )}
    </div>
  );
}

function CodeBlock({ block, updateBlock }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded font-mono">
          Go
        </span>
        <span>Code Block</span>
      </div>
      <textarea
        value={block.value || ""}
        onChange={(e) => updateBlock(block.id, { value: e.target.value })}
        placeholder={`// Enter your Go code here...\nfunc main() {\n    fmt.Println("Hello, World!")\n}`}
        rows="8"
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-mono text-sm bg-gray-900 text-green-400 transition-all resize-y"
        spellCheck="false"
      />
    </div>
  );
}

function TableBlock({ block, updateBlock }) {
  const rows = block.rows || [["", ""], ["", ""]];

  const updateCell = (rowIndex, colIndex, value) => {
    const newRows = [...rows];
    newRows[rowIndex] = [...newRows[rowIndex]];
    newRows[rowIndex][colIndex] = value;
    updateBlock(block.id, { rows: newRows });
  };

  const addRow = () => {
    const colCount = rows[0]?.length || 2;
    const newRow = Array(colCount).fill("");
    updateBlock(block.id, { rows: [...rows, newRow] });
  };

  const removeRow = () => {
    if (rows.length <= 1) return;
    updateBlock(block.id, { rows: rows.slice(0, -1) });
  };

  const addColumn = () => {
    const newRows = rows.map(row => [...row, ""]);
    updateBlock(block.id, { rows: newRows });
  };

  const removeColumn = () => {
    if (rows[0].length <= 1) return;
    const newRows = rows.map(row => row.slice(0, -1));
    updateBlock(block.id, { rows: newRows });
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap gap-2 mb-2">
        <button
          type="button"
          onClick={addRow}
          className="flex items-center gap-1 px-2 py-1 bg-teal-50 text-teal-700 border border-teal-200 rounded text-xs hover:bg-teal-100"
        >
          <FaPlus size={10} /> Add Row
        </button>
        <button
          type="button"
          onClick={removeRow}
          disabled={rows.length <= 1}
          className="flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 border border-red-200 rounded text-xs hover:bg-red-100 disabled:opacity-50"
        >
          <FaMinus size={10} /> Remove Row
        </button>
        <div className="w-px h-6 bg-gray-300 mx-1"></div>
        <button
          type="button"
          onClick={addColumn}
          className="flex items-center gap-1 px-2 py-1 bg-teal-50 text-teal-700 border border-teal-200 rounded text-xs hover:bg-teal-100"
        >
          <FaColumns size={10} /> <FaPlus size={8} /> Add Col
        </button>
        <button
          type="button"
          onClick={removeColumn}
          disabled={rows[0].length <= 1}
          className="flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 border border-red-200 rounded text-xs hover:bg-red-100 disabled:opacity-50"
        >
          <FaColumns size={10} /> <FaMinus size={8} /> Remove Col
        </button>
      </div>

      {/* Editor Grid */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-sm text-left">
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className={rowIndex === 0 ? "bg-gray-100 font-semibold" : "border-t"}>
                {row.map((cell, colIndex) => (
                  <td key={colIndex} className="p-1 border-r last:border-r-0 min-w-[100px]">
                    <input
                      type="text"
                      value={cell}
                      onChange={(e) => updateCell(rowIndex, colIndex, e.target.value)}
                      placeholder={rowIndex === 0 ? `Header ${colIndex + 1}` : "Cell"}
                      className="w-full px-2 py-1.5 bg-transparent focus:bg-white focus:outline-none focus:ring-1 focus:ring-teal-500 rounded"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-500 text-center mt-1">
        First row is the header.
      </p>
    </div>
  );
}
